// src/components/chat/MarkdownTable.tsx
// Rich renderer for GFM markdown tables inside chat messages.
// Handles any column/row count: auto-detects numeric columns, right-aligns them,
// colors signed deltas, supports sort / copy (TSV, Markdown) / download (CSV),
// and caps very long tables with a "show all" toggle.

import React, { Children, isValidElement, type ReactNode, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ArrowUpDown, ChevronDown, ChevronUp, ChevronsDown, Copy, Download } from 'lucide-react';
import toast from 'react-hot-toast';

type ColumnType = 'currency' | 'percent' | 'number' | 'text';
type SortDir = 'asc' | 'desc';

interface Cell {
  node: ReactNode;
  text: string;
}

interface ParsedTable {
  headers: Cell[];
  rows: Cell[][];
}

const DEFAULT_ROW_CAP = 50;

// ---------------------------------------------------------------------------
// Parsing react-markdown's rendered table tree
// ---------------------------------------------------------------------------

function nodeToText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (isValidElement(node)) {
    return nodeToText((node.props as { children?: ReactNode }).children);
  }
  return '';
}

function collectCells(rowChildren: ReactNode): Cell[] {
  const cells: Cell[] = [];
  Children.forEach(rowChildren, (cellNode) => {
    if (!isValidElement(cellNode)) return;
    if (cellNode.type !== 'th' && cellNode.type !== 'td') return;
    const content = (cellNode.props as { children?: ReactNode }).children;
    cells.push({ node: content, text: nodeToText(content).trim() });
  });
  return cells;
}

function parseTable(children: ReactNode): ParsedTable {
  const headers: Cell[] = [];
  const rows: Cell[][] = [];

  Children.forEach(children, (section) => {
    if (!isValidElement(section)) return;
    const sectionChildren = (section.props as { children?: ReactNode }).children;

    if (section.type === 'thead') {
      let taken = false;
      Children.forEach(sectionChildren, (row) => {
        if (taken || !isValidElement(row) || row.type !== 'tr') return;
        headers.push(...collectCells((row.props as { children?: ReactNode }).children));
        taken = true;
      });
    } else if (section.type === 'tbody') {
      Children.forEach(sectionChildren, (row) => {
        if (!isValidElement(row) || row.type !== 'tr') return;
        const cells = collectCells((row.props as { children?: ReactNode }).children);
        if (cells.length > 0) rows.push(cells);
      });
    }
  });

  return { headers, rows };
}

// ---------------------------------------------------------------------------
// Column type detection & numeric parsing
// ---------------------------------------------------------------------------

const CURRENCY_RE = /[$€£¥₹]/;
const PERCENT_SUFFIX_RE = /%\s*$/;
// Accept numbers with optional sign (including unicode minus "−"), optional
// currency symbol, digits with thousands separators, optional decimal, optional
// % suffix, and optional trailing parenthetical (e.g. "+$1,673 (+9.7%)").
const NUMERIC_SHAPE_RE = /^[+−-]?\s*[$€£¥₹]?\s*[\d,]+(\.\d+)?\s*%?(\s*\([^)]*\))?$/;
const EMPTY_MARKERS = new Set(['', '-', '—', '–', 'n/a', 'na']);

function parseNumeric(text: string): number | null {
  if (!text) return null;
  const match = text.replace(/−/g, '-').match(/[+-]?[\d,]+(\.\d+)?/);
  if (!match) return null;
  const n = parseFloat(match[0].replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
}

function detectColumnType(rows: Cell[][], colIdx: number): ColumnType {
  const samples = rows
    .map((r) => (r[colIdx]?.text ?? '').trim())
    .filter((t) => !EMPTY_MARKERS.has(t.toLowerCase()));

  if (samples.length === 0) return 'text';

  if (samples.every((s) => CURRENCY_RE.test(s) && parseNumeric(s) !== null)) return 'currency';
  if (samples.every((s) => PERCENT_SUFFIX_RE.test(s) && parseNumeric(s) !== null)) return 'percent';
  if (samples.every((s) => parseNumeric(s) !== null && NUMERIC_SHAPE_RE.test(s.replace(/\s+/g, ' ')))) return 'number';

  return 'text';
}

// ---------------------------------------------------------------------------
// Delta coloring (applies to cells inside numeric columns only)
// ---------------------------------------------------------------------------

function deltaTone(text: string): 'positive' | 'negative' | 'neutral' | null {
  const trimmed = text.trim().replace(/−/g, '-');
  if (!trimmed) return null;

  const signMatch = trimmed.match(/^([+-])\s*(.*)$/);
  if (!signMatch) {
    // Unsigned zero reads as "no change" — shown muted when it's in a delta-ish column.
    if (/^0+(\.0+)?(\s*(%|pts?|points?|pp))?$/i.test(trimmed)) return 'neutral';
    return null;
  }

  const sign = signMatch[1];
  const rest = signMatch[2];
  if (!/\d/.test(rest)) return null;

  const n = parseNumeric(trimmed);
  if (n === 0) return 'neutral';
  return sign === '+' ? 'positive' : 'negative';
}

// ---------------------------------------------------------------------------
// Export helpers (TSV / CSV / Markdown)
// ---------------------------------------------------------------------------

function toTSV(headers: Cell[], rows: Cell[][]): string {
  const lines: string[] = [headers.map((h) => h.text).join('\t')];
  for (const row of rows) lines.push(row.map((c) => c.text).join('\t'));
  return lines.join('\n');
}

function csvEscape(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function toCSV(headers: Cell[], rows: Cell[][]): string {
  const lines: string[] = [headers.map((h) => csvEscape(h.text)).join(',')];
  for (const row of rows) lines.push(row.map((c) => csvEscape(c.text)).join(','));
  return lines.join('\n');
}

function toMarkdown(headers: Cell[], rows: Cell[][]): string {
  const escape = (s: string): string => s.replace(/\|/g, '\\|').replace(/\n/g, ' ');
  const lines: string[] = [
    '| ' + headers.map((h) => escape(h.text)).join(' | ') + ' |',
    '| ' + headers.map(() => '---').join(' | ') + ' |',
  ];
  for (const row of rows) lines.push('| ' + row.map((c) => escape(c.text)).join(' | ') + ' |');
  return lines.join('\n');
}

async function copyText(text: string, label: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error('Copy failed');
  }
}

function downloadCSV(headers: Cell[], rows: Cell[][]): void {
  const csv = toCSV(headers, rows);
  // UTF-8 BOM so Excel opens accented characters correctly.
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  a.href = url;
  a.download = `table-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MarkdownTableProps {
  children?: ReactNode;
}

const MarkdownTable: React.FC<MarkdownTableProps> = ({ children }) => {
  const parsed = useMemo(() => parseTable(children), [children]);
  const { headers, rows } = parsed;

  const columnTypes = useMemo<ColumnType[]>(
    () => headers.map((_, colIdx) => detectColumnType(rows, colIdx)),
    [headers, rows]
  );

  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showAll, setShowAll] = useState(false);

  const sortedRows = useMemo(() => {
    if (sortCol === null) return rows;
    const colType = columnTypes[sortCol];
    const copy = [...rows];
    copy.sort((a, b) => {
      const aText = a[sortCol]?.text ?? '';
      const bText = b[sortCol]?.text ?? '';
      if (colType !== 'text') {
        const aNum = parseNumeric(aText);
        const bNum = parseNumeric(bText);
        if (aNum === null && bNum === null) return 0;
        if (aNum === null) return 1;
        if (bNum === null) return -1;
        return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
      }
      return sortDir === 'asc' ? aText.localeCompare(bText) : bText.localeCompare(aText);
    });
    return copy;
  }, [rows, sortCol, sortDir, columnTypes]);

  const overCap = sortedRows.length > DEFAULT_ROW_CAP;
  const visibleRows = overCap && !showAll ? sortedRows.slice(0, DEFAULT_ROW_CAP) : sortedRows;

  // Fall back to a plain table if parsing produced nothing — preserves the
  // user's content even if the AST shape is unexpected.
  if (headers.length === 0) {
    return (
      <div className="overflow-x-auto my-3">
        <table className="w-full border-collapse text-xs">{children}</table>
      </div>
    );
  }

  const handleSort = (colIdx: number): void => {
    if (sortCol === colIdx) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(colIdx);
      setSortDir('asc');
    }
  };

  return (
    <div className="my-3 rounded-lg border border-border overflow-hidden bg-background not-prose">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-border bg-muted/50">
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {rows.length} row{rows.length === 1 ? '' : 's'} × {headers.length} col{headers.length === 1 ? '' : 's'}
        </span>
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            icon={<Copy className="w-3 h-3" />}
            label="TSV"
            title="Copy as TSV (paste into Excel / Google Sheets)"
            onClick={() => void copyText(toTSV(headers, sortedRows), 'TSV')}
          />
          <ToolbarButton
            icon={<Copy className="w-3 h-3" />}
            label="MD"
            title="Copy as Markdown"
            onClick={() => void copyText(toMarkdown(headers, sortedRows), 'Markdown')}
          />
          <ToolbarButton
            icon={<Download className="w-3 h-3" />}
            label="CSV"
            title="Download as CSV"
            onClick={() => downloadCSV(headers, sortedRows)}
          />
        </div>
      </div>

      {/* Scroll container — gains vertical scroll + sticky header only when expanded past cap */}
      <div className={clsx('overflow-x-auto', showAll && overCap && 'max-h-[60vh] overflow-y-auto')}>
        <table className="w-full border-collapse text-xs">
          <thead className={clsx('bg-muted', showAll && overCap && 'sticky top-0 z-10')}>
            <tr>
              {headers.map((h, colIdx) => {
                const rightAlign = columnTypes[colIdx] !== 'text';
                const isSorted = sortCol === colIdx;
                return (
                  <th
                    key={colIdx}
                    onClick={() => handleSort(colIdx)}
                    className={clsx(
                      'px-3 py-2 border-b border-border font-semibold text-foreground cursor-pointer select-none whitespace-nowrap hover:bg-accent transition-colors',
                      rightAlign ? 'text-right' : 'text-left'
                    )}
                  >
                    <span className="inline-flex items-center gap-1 align-middle">
                      <span>{h.node}</span>
                      {isSorted ? (
                        sortDir === 'asc'
                          ? <ChevronUp className="w-3 h-3 opacity-70 flex-shrink-0" />
                          : <ChevronDown className="w-3 h-3 opacity-70 flex-shrink-0" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-20 flex-shrink-0" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className="even:bg-muted/40 hover:bg-accent/60 transition-colors"
              >
                {row.map((cell, colIdx) => {
                  const colType = columnTypes[colIdx];
                  const rightAlign = colType !== 'text';
                  const tone = colType !== 'text' ? deltaTone(cell.text) : null;
                  return (
                    <td
                      key={colIdx}
                      className={clsx(
                        'px-3 py-1.5 border-b border-border text-foreground align-top',
                        rightAlign ? 'text-right tabular-nums' : 'text-left',
                        tone === 'positive' && 'text-success',
                        tone === 'negative' && 'text-negative',
                        tone === 'neutral' && 'text-muted-foreground'
                      )}
                    >
                      {cell.node}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Row cap footer */}
      {overCap && (
        <button
          onClick={() => setShowAll((prev) => !prev)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] text-info hover:bg-info/15 border-t border-border transition-colors"
        >
          <ChevronsDown className={clsx('w-3 h-3 transition-transform', showAll && 'rotate-180')} />
          {showAll ? `Collapse to first ${DEFAULT_ROW_CAP}` : `Show all ${rows.length} rows`}
        </button>
      )}
    </div>
  );
};

interface ToolbarButtonProps {
  icon: ReactNode;
  label: string;
  title: string;
  onClick: () => void;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ icon, label, title, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors"
  >
    {icon}
    <span>{label}</span>
  </button>
);

export default MarkdownTable;
